import React, { useRef, useEffect, useCallback } from 'react';
import { TextField, Text, Spinner, Icon, ITextField, Link, PrimaryButton } from '@fluentui/react';
import { useGetCustomersList } from 'app/services/search-api';
import { Trans } from 'react-i18next';
import {
    ICustomerResult,
    MSX_SEARCH_TIMEOUT_MS,
    MIN_SEARCH_LENGTH,
} from './types';
import { MsxSuggestDocument } from 'generated/search-backend';
import { TelemetryConstants } from 'app/utils/constants';
import { logFlowEvent } from '../flowTelemetry';
import { ViewTelemetryConstants, VIEW_TELEMETRY_SECTION } from 'app/modules/newTicketView/telemetryConstants';
import { SpeechTokenFetcher, useSpeechRecognition } from 'app/utils/useSpeechRecognition';
import './SearchMSXGrouped.scss';

const CUSTOMER_IMPACT_SECTION = 'Customer Impact';
const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS_PER_GROUP = 5;

interface IProps {
    onOpportunitySelect: (opportunity: MsxSuggestDocument) => void;
    onCustomerSelect: (customer: ICustomerResult) => void;
    onAddNewCustomer: () => void;
    getSpeechToTextToken: SpeechTokenFetcher;
}

/**
 * Unified MSX search component with grouped dropdown.
 * Groups results into "Opportunities" and "Customers" sections.
 */
export const SearchMSXGrouped: React.FC<IProps> = ({
    onOpportunitySelect,
    onCustomerSelect,
    onAddNewCustomer,
    getSpeechToTextToken,
}) => {
    const handleOptionKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>,
        onActivate: () => void,
    ) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onActivate();
        }
    };
    const [searchValue, setSearchValue] = React.useState('');
    const [showSuggestion, setShowSuggestion] = React.useState(false);
    const [timedOut, setTimedOut] = React.useState(false);
    const [isQueryPending, setIsQueryPending] = React.useState(false);

    const { data, mutate: getCustomersList, isLoading, isError } = useGetCustomersList();
    const suggestionRef = useRef<HTMLDivElement>(null);
    const textInput = useRef<ITextField>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchMetaRef = useRef<{ query: string; startMs: number } | null>(null);
    const searchSequenceRef = useRef(0);

    // Dismiss suggestion box on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent | KeyboardEvent) {
            if (suggestionRef.current && !suggestionRef.current.contains(event.target as Node)) {
                setShowSuggestion(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const executeSearch = useCallback(
        (value: string, searchSequence: number) => {
            timeoutRef.current = setTimeout(() => {
                setTimedOut(true);
                setIsQueryPending(false);
            }, MSX_SEARCH_TIMEOUT_MS);
            searchMetaRef.current = { query: value, startMs: performance.now() };
            getCustomersList(
                { searchValue: value },
                {
                    onSettled: () => {
                        if (searchSequenceRef.current === searchSequence) {
                            setIsQueryPending(false);
                        }
                    }
                }
            );
        },
        [getCustomersList],
    );

    const handleSearch = useCallback(
        (_e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            const value = newValue || '';
            setSearchValue(value);
            setTimedOut(false);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            const searchSequence = ++searchSequenceRef.current;

            if (value.length >= MIN_SEARCH_LENGTH) {
                setIsQueryPending(true);
                setShowSuggestion(true);
                debounceRef.current = setTimeout(
                    () => executeSearch(value, searchSequence),
                    SEARCH_DEBOUNCE_MS,
                );
            } else {
                setIsQueryPending(false);
                setShowSuggestion(false);
            }
        },
        [executeSearch],
    );

    const handleSearchKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            event.stopPropagation();
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            const searchSequence = ++searchSequenceRef.current;
            setTimedOut(false);

            if (searchValue.length >= MIN_SEARCH_LENGTH) {
                setIsQueryPending(true);
                setShowSuggestion(true);
                executeSearch(searchValue, searchSequence);
            } else {
                setIsQueryPending(false);
                setShowSuggestion(false);
            }
        },
        [executeSearch, searchValue],
    );

    // Clear timeout when data arrives
    useEffect(() => {
        if ((data || isError) && !isLoading && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [data, isError, isLoading]);

    // MSX search timed out (10s) — surface a failure event (Req 21)
    useEffect(() => {
        if (timedOut) {
            logFlowEvent(TelemetryConstants.CUSTOMER_MSX_FAILURE, CUSTOMER_IMPACT_SECTION, {
                errorType: 'timeout',
                queryLength: searchMetaRef.current?.query?.length ?? 0,
            });
            logFlowEvent(ViewTelemetryConstants.MSX_SEARCH_FAILED, VIEW_TELEMETRY_SECTION, {
                errorType: 'timeout',
                queryLength: searchMetaRef.current?.query?.length ?? 0,
            });
        }
    }, [timedOut]);

    useEffect(() => {
        if (isError && !timedOut) {
            logFlowEvent(TelemetryConstants.CUSTOMER_MSX_FAILURE, CUSTOMER_IMPACT_SECTION, {
                errorType: 'request',
                queryLength: searchMetaRef.current?.query?.length ?? 0,
            });
            logFlowEvent(ViewTelemetryConstants.MSX_SEARCH_FAILED, VIEW_TELEMETRY_SECTION, {
                errorType: 'request',
                queryLength: searchMetaRef.current?.query?.length ?? 0,
            });
        }
    }, [isError, timedOut]);

    // Group results into opportunities and customers
    const groupedResults = React.useMemo(() => {
        const items: MsxSuggestDocument[] = (data ?? [])
            .map((item) => item.document)
            .filter((document): document is MsxSuggestDocument => document !== undefined);
        const opportunities = items.filter((item) => item.opportunityNumber).slice(0, MAX_RESULTS_PER_GROUP);

        // Deduplicate customers by AccountName
        const customerMap = new Map<string, ICustomerResult>();
        items.forEach((item) => {
            if (item.accountName && !customerMap.has(item.accountName)) {
                customerMap.set(item.accountName, {
                    AccountName: item.accountName,
                    Country: item.country ?? '',
                });
            }
        });

        return {
            opportunities,
            customers: Array.from(customerMap.values()).slice(0, MAX_RESULTS_PER_GROUP),
        };
    }, [data]);

    // Emit search-effectiveness telemetry once results settle (6.2 / 6.3)
    useEffect(() => {
        const meta = searchMetaRef.current;
        if (!data || isLoading || !meta) return;
        const responseTime = Math.round(performance.now() - meta.startMs);
        const oppCount = groupedResults.opportunities.length;
        const custCount = groupedResults.customers.length;
        logFlowEvent(TelemetryConstants.CUSTOMER_SEARCH_EXECUTED, CUSTOMER_IMPACT_SECTION, {
            queryLength: meta.query.length,
            opportunityResults: oppCount,
            customerResults: custCount,
            responseTime,
        });
        if (oppCount === 0 && custCount === 0) {
            logFlowEvent(TelemetryConstants.CUSTOMER_SEARCH_ZERO_RESULTS, CUSTOMER_IMPACT_SECTION, {
                queryLength: meta.query.length,
            });
        }
        searchMetaRef.current = null;
    }, [data, isLoading, groupedResults]);

    const handleSelectOpportunity = (item: MsxSuggestDocument) => {
        setSearchValue('');
        setShowSuggestion(false);
        onOpportunitySelect(item);
    };

    const handleSelectCustomer = (customer: ICustomerResult) => {
        setSearchValue('');
        setShowSuggestion(false);
        onCustomerSelect(customer);
    };

    const hasResults =
        groupedResults.opportunities.length > 0 || groupedResults.customers.length > 0;

    const handleRecognized = useCallback((text: string) => {
        handleSearch({} as React.FormEvent<HTMLInputElement>, text);
    }, [handleSearch]);
    const { isRecording, speechError, toggle: toggleSpeech } = useSpeechRecognition(
        getSpeechToTextToken,
        handleRecognized,
    );
    const handleMicClick = useCallback(() => {
        logFlowEvent(
            isRecording ? TelemetryConstants.VOICE_STOPPED : TelemetryConstants.VOICE_STARTED,
            CUSTOMER_IMPACT_SECTION,
            { context: 'customerSearch' },
        );
        toggleSpeech();
    }, [isRecording, toggleSpeech]);

    return (
        <div className="search-msx-grouped" ref={suggestionRef}>
            <label className="field-label">
                <Trans>Search customer and opportunity</Trans>
            </label>
            <div className="search-input-wrapper">
                <TextField
                    data-testid="msx-customer-search-input"
                    className={showSuggestion ? 'active' : ''}
                    value={searchValue}
                    onChange={handleSearch}
                    onKeyDown={handleSearchKeyDown}
                    disabled={timedOut || isError}
                    placeholder="e.g., Contoso or 7-XCS8HCFV"
                    autoComplete="off"
                    componentRef={textInput}
                    tabIndex={0}
                    ariaLabel="Search by MSX Opportunity ID, Account ID, or Customer Name"
                    onRenderPrefix={() => (
                        <Icon iconName="Search" className="search-icon" />
                    )}
                    onRenderSuffix={() => (
                        <button
                            className={`search-bar-mic ${isRecording ? 'search-bar-mic--active' : ''}`}
                            onClick={handleMicClick}
                            aria-label={isRecording ? 'Stop recording' : 'Start voice search'}
                            type="button"
                        >
                            {isRecording ? (
                                <div className="recording-indicator">
                                    <div className="recording-dot" />
                                    <div className="recording-dot" />
                                    <div className="recording-dot" />
                                </div>
                            ) : (
                                <Icon iconName="Microphone" className="mic-icon" />
                            )}
                        </button>
                    )}
                />
            </div>
            {speechError && <div className="msx-error-text" role="alert">{speechError}</div>}

            {showSuggestion && (
                <div
                    className="suggest-cont-box"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {(isQueryPending || isLoading) && !timedOut && !isError ? (
                        <div className="p-a-20">
                            <Spinner label="Searching MSX…" />
                        </div>
                    ) : timedOut || isError ? (
                        <div className="msx-error-state p-a-20" role="alert">
                            <Text className="msx-error-text">
                                We can't fetch customers from MSX right now. Search has been disabled. You can still add a customer manually.
                            </Text>
                            <PrimaryButton
                                data-testid="msx-manual-add-error"
                                text="Add new customer"
                                onClick={onAddNewCustomer}
                                ariaLabel="Add new customer manually"
                            />
                        </div>
                    ) : !isQueryPending && !isLoading && !hasResults ? (
                        <div className="no-results p-a-20">
                            <Text className="no-results-text">
                                No matches for {searchValue}
                            </Text>
                            <Text>Try a different search term, or add the customer manually below</Text>
                            <PrimaryButton
                                data-testid="msx-manual-add-empty"
                                text="Add new customer"
                                onClick={onAddNewCustomer}
                                ariaLabel="Add new customer manually"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Opportunities Section */}
                            {groupedResults.opportunities.length > 0 && (
                                <div className="result-group">
                                    <div className="group-header">
                                        <Text className="group-label">
                                            <Trans>Opportunities</Trans>
                                        </Text>
                                    </div>
                                    <div role="listbox" aria-label="Opportunity suggestions">
                                    {groupedResults.opportunities.map((item, index) => (
                                        <div
                                            key={item.opportunityId}
                                            tabIndex={0}
                                            className="suggestion-item"
                                            data-testid="msx-opportunity-suggestion"
                                            role="option"
                                            aria-selected="false"
                                            aria-posinset={index + 1}
                                            aria-setsize={groupedResults.opportunities.length}
                                            onClick={() => handleSelectOpportunity(item)}
                                            onKeyDown={(e) => handleOptionKeyDown(
                                                e,
                                                () => handleSelectOpportunity(item),
                                            )}
                                            aria-label={`Opportunity: ${item.opportunityNumber} - ${item.accountName} - ${item.country}`}
                                        >
                                            <Text>
                                                {`${item.opportunityNumber} - ${item.accountName} - ${item.country} - ${item.topic?.substring(0, 50)}`}
                                            </Text>
                                            <Icon
                                                iconName="VerifiedBrandSolid"
                                                className="verified-icon"
                                            />
                                        </div>
                                    ))}
                                    </div>
                                </div>
                            )}

                            {/* Customers Section */}
                            {groupedResults.customers.length > 0 && (
                                <div className="result-group">
                                    <div className="group-header">
                                        <Text className="group-label">
                                            <Trans>Customer</Trans>
                                        </Text>
                                    </div>
                                    <div role="listbox" aria-label="Customer suggestions">
                                    {groupedResults.customers.map((customer, index) => (
                                        <div
                                            key={customer.AccountName}
                                            tabIndex={0}
                                            className="suggestion-item"
                                            role="option"
                                            aria-selected="false"
                                            aria-posinset={index + 1}
                                            aria-setsize={groupedResults.customers.length}
                                            onClick={() => handleSelectCustomer(customer)}
                                            onKeyDown={(e) => handleOptionKeyDown(
                                                e,
                                                () => handleSelectCustomer(customer),
                                            )}
                                            aria-label={`Customer: ${customer.AccountName}`}
                                        >
                                            <Text>{customer.AccountName}</Text>
                                        </div>
                                    ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {hasResults && !isLoading && !timedOut && !isError && (
                        <div className="fallback-section">
                            <Text className="fallback-text">
                                <Trans>Can't find what you're looking for?</Trans>
                            </Text>
                            <Link
                                data-testid="msx-manual-add-link"
                                className="add-new-customer-btn"
                                onClick={onAddNewCustomer}
                                ariaLabel="Add new customer manually"
                            >
                                <Trans>Add new customer</Trans>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
